import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../context/AppContext";
import Navbar from "../components/Navbar";
import ProductCard from "../components/ProductCard";
import Footer from "../components/Footer";

/* ── Section colour config — known sections + auto palette for new ones ── */
const KNOWN_STYLES = {
  juices: {
    color: "#2D8B4E",
    glassActive: "rgba(45,139,78,0.22)",
    lightBg: "rgba(45,139,78,0.08)",
  },
  salads: {
    color: "#16a34a",
    glassActive: "rgba(22,163,74,0.22)",
    lightBg: "rgba(22,163,74,0.08)",
  },
  sandwiches: {
    color: "#d97706",
    glassActive: "rgba(217,119,6,0.22)",
    lightBg: "rgba(217,119,6,0.08)",
  },
  smoothies: {
    color: "#7c3aed",
    glassActive: "rgba(124,58,237,0.22)",
    lightBg: "rgba(124,58,237,0.08)",
  },
};
const AUTO_PALETTES = [
  {
    color: "#0891b2",
    glassActive: "rgba(8,145,178,0.22)",
    lightBg: "rgba(8,145,178,0.08)",
  },
  {
    color: "#be185d",
    glassActive: "rgba(190,24,93,0.22)",
    lightBg: "rgba(190,24,93,0.08)",
  },
  {
    color: "#0f766e",
    glassActive: "rgba(15,118,110,0.22)",
    lightBg: "rgba(15,118,110,0.08)",
  },
  {
    color: "#b45309",
    glassActive: "rgba(180,83,9,0.22)",
    lightBg: "rgba(180,83,9,0.08)",
  },
  {
    color: "#6d28d9",
    glassActive: "rgba(109,40,217,0.22)",
    lightBg: "rgba(109,40,217,0.08)",
  },
  {
    color: "#0e7490",
    glassActive: "rgba(14,116,144,0.22)",
    lightBg: "rgba(14,116,144,0.08)",
  },
];
let _pi = 0;
const _cache = {};
const getStyle = (key) => {
  if (KNOWN_STYLES[key]) return KNOWN_STYLES[key];
  if (!_cache[key]) {
    _cache[key] = AUTO_PALETTES[_pi % AUTO_PALETTES.length];
    _pi++;
  }
  return _cache[key];
};

const DEBOUNCE_MS = 350;

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  /* ── sections data from backend ── */
  const [sections, setSections] = useState([]); // full section tree from /sections
  const [sectionsErr, setSectionsErr] = useState(false);
  const [sectionsReady, setSectionsReady] = useState(false);

  /* ── active filters ── */
  const [activeSection, setActiveSection] = useState(
    searchParams.get("section") || "juices",
  );
  const [activeCategory, setActiveCategory] = useState(
    searchParams.get("category") || "all",
  );
  const [bestseller, setBestseller] = useState(
    searchParams.get("bestseller") === "true",
  );
  const [searchInput, setSearchInput] = useState("");
  const [debSearch, setDebSearch] = useState("");

  /* ── products ── */
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searching, setSearching] = useState(false);

  const abortRef = useRef(null);

  /* ══ 1. Fetch sections tree from backend ══════════════════════ */
  useEffect(() => {
    api
      .get("/products/sections")
      .then((res) => {
        const data = res.data?.sections || [];
        // attach style per section
        const enriched = data.map((sec) => ({
          ...sec,
          color: getStyle(sec.key).color,
          lightBg: getStyle(sec.key).lightBg,
          glassActive: getStyle(sec.key).glassActive,
        }));
        setSections(enriched);
        setSectionsReady(true);
      })
      .catch(() => {
        setSectionsErr(true);
        setSectionsReady(true);
      });
  }, []);

  /* ══ 2. Search debounce ═══════════════════════════════════════ */
  useEffect(() => {
    if (searchInput === debSearch) return;
    setSearching(true);
    const t = setTimeout(() => {
      setDebSearch(searchInput);
      setSearching(false);
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
      setSearching(false);
    };
  }, [searchInput, debSearch]);

  /* ══ 3. Fetch products whenever filters change ════════════════ */
  const fetchProducts = useCallback(async () => {
    if (!sectionsReady) return;

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError("");

    try {
      const p = new URLSearchParams();

      if (activeCategory !== "all") {
        p.set("category", activeCategory);
      } else {
        p.set("section", activeSection);
      }

      if (bestseller) p.set("bestseller", "true");
      if (debSearch.trim()) p.set("search", debSearch.trim());

      const res = await api.get(`/products?${p}`, { signal: ctrl.signal });
      setProducts(
        Array.isArray(res.data) ? res.data : res.data?.products || [],
      );
    } catch (err) {
      if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
      setError("Failed to load products. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [sectionsReady, activeSection, activeCategory, bestseller, debSearch]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  /* ══ Helpers ══════════════════════════════════════════════════ */
  const currentSection = sections.find((s) => s.key === activeSection) || null;

  const handleSectionChange = (key) => {
    setActiveSection(key);
    setActiveCategory("all");
    setBestseller(false);
    setSearchInput("");
    setDebSearch("");
    setSearchParams({ section: key });
  };

  const handleCategoryChange = (key) => {
    setActiveCategory(key);
    setBestseller(false);
    setSearchInput("");
    setDebSearch("");
    const p = { section: activeSection };
    if (key !== "all") p.category = key;
    setSearchParams(p);
  };

  const activeCatLabel =
    activeCategory === "all"
      ? `All ${currentSection?.label || ""}`
      : currentSection?.categories?.find((c) => c.key === activeCategory)
          ?.label || activeCategory;

  /* ══ RENDER ═══════════════════════════════════════════════════ */
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#f8fafb",
        fontFamily: "'Roboto', sans-serif",
      }}
    >
      <Navbar />

      {/* ── HEADER ── */}
      <div
        style={{
          background:
            "linear-gradient(135deg,#0a3d1f 0%,#0f5230 45%,#1a7040 100%)",
          padding: "24px 20px 0",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* blobs */}
        <div
          style={{
            position: "absolute",
            top: -60,
            right: -60,
            width: 240,
            height: 240,
            borderRadius: "50%",
            background: "rgba(76,175,114,0.08)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -40,
            left: -40,
            width: 180,
            height: 180,
            borderRadius: "50%",
            background: "rgba(255,179,71,0.06)",
            pointerEvents: "none",
          }}
        />

        <div style={{ maxWidth: 1152, margin: "0 auto", position: "relative" }}>
          {/* title + search row */}
          <div
            className="hdr-top"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 20,
            }}
          >
            <div>
              <h1
                style={{
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: "clamp(20px,3.5vw,28px)",
                  margin: "0 0 3px",
                  letterSpacing: "-0.5px",
                  lineHeight: 1.1,
                }}
              >
                Our Fresh Menu 🥤
              </h1>
              <p
                style={{
                  color: "rgba(255,255,255,0.45)",
                  fontSize: 10,
                  margin: 0,
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
              >
                Natural · Fresh · No Preservatives
              </p>
            </div>

            {/* desktop search */}
            <div
              className="hdr-search"
              style={{ position: "relative", flexShrink: 0, width: 250 }}
            >
              <span
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 13,
                  opacity: 0.5,
                  pointerEvents: "none",
                }}
              >
                {searching ? "⏳" : "🔍"}
              </span>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={`Search ${currentSection?.label?.toLowerCase() || "menu"}…`}
                style={{
                  width: "100%",
                  padding: "10px 34px 10px 34px",
                  borderRadius: 12,
                  border: "1.5px solid rgba(255,255,255,0.18)",
                  outline: "none",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#111",
                  background: "rgba(255,255,255,0.92)",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => {
                  e.target.style.background = "#fff";
                  e.target.style.borderColor = "rgba(255,255,255,0.6)";
                  e.target.style.boxShadow = "0 0 0 3px rgba(255,255,255,0.12)";
                }}
                onBlur={(e) => {
                  e.target.style.background = "rgba(255,255,255,0.92)";
                  e.target.style.borderColor = "rgba(255,255,255,0.18)";
                  e.target.style.boxShadow = "none";
                }}
              />
              {searchInput && (
                <button
                  onClick={() => {
                    setSearchInput("");
                    setDebSearch("");
                  }}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 14,
                    color: "#9ca3af",
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          {/* mobile search */}
          <div
            className="mob-search"
            style={{ display: "none", position: "relative", marginBottom: 8 }}
          >
            <span
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 13,
                opacity: 0.5,
                pointerEvents: "none",
              }}
            >
              {searching ? "⏳" : "🔍"}
            </span>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={`Search ${currentSection?.label?.toLowerCase() || "menu"}…`}
              style={{
                width: "100%",
                padding: "11px 36px 11px 36px",
                borderRadius: 12,
                border: "1.5px solid rgba(255,255,255,0.18)",
                outline: "none",
                fontSize: 13.5,
                fontWeight: 500,
                color: "#111",
                background: "rgba(255,255,255,0.92)",
                boxSizing: "border-box",
              }}
            />
            {searchInput && (
              <button
                onClick={() => {
                  setSearchInput("");
                  setDebSearch("");
                }}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 15,
                  color: "#9ca3af",
                }}
              >
                ✕
              </button>
            )}
          </div>{" "}
          {/* ── SECTION TABS ── */}
          <div
            className="tabs-wrap"
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "nowrap",
              overflowX: "auto",
              scrollbarWidth: "none",
              paddingBottom: 0,
            }}
          >
            {!sectionsReady
              ? [1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="sec-tab-skel"
                    style={{
                      height: 44,
                      width: 110,
                      borderRadius: "12px 12px 0 0",
                      background: "rgba(255,255,255,0.08)",
                      flexShrink: 0,
                      animation: "pulse 1.4s ease infinite",
                    }}
                  />
                ))
              : sections.map((sec) => {
                  const active = sec.key === activeSection;
                  return (
                    <button
                      key={sec.key}
                      onClick={() => handleSectionChange(sec.key)}
                      className="sec-tab"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "10px 18px 12px",
                        borderRadius: "12px 12px 0 0",
                        border: "1.5px solid",
                        borderColor: active
                          ? "rgba(255,255,255,0.45)"
                          : "rgba(255,255,255,0.1)",
                        borderBottom: active
                          ? "2.5px solid #fff"
                          : "2.5px solid transparent",
                        background: active
                          ? "rgba(255,255,255,0.15)"
                          : "rgba(255,255,255,0.05)",
                        backdropFilter: active ? "blur(8px)" : "none",
                        cursor: "pointer",
                        color: active ? "#fff" : "rgba(255,255,255,0.52)",
                        fontWeight: active ? 800 : 600,
                        fontSize: 13.5,
                        transition: "all 0.2s ease",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        boxShadow: active
                          ? "0 -2px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.2)"
                          : "none",
                        letterSpacing: "-0.1px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 18,
                          lineHeight: 1,
                          display: "inline-block",
                          transform: active ? "scale(1.2)" : "scale(1)",
                          transition:
                            "transform 0.25s cubic-bezier(0.34,1.56,0.64,1)",
                        }}
                      >
                        {sec.emoji}
                      </span>
                      <span className="tab-label">{sec.label}</span>
                      {sec.totalCount > 0 && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 900,
                            lineHeight: 1,
                            padding: "2px 6px",
                            borderRadius: 20,
                            background: active
                              ? "rgba(255,255,255,0.25)"
                              : "rgba(255,255,255,0.1)",
                            color: active ? "#fff" : "rgba(255,255,255,0.65)",
                          }}
                        >
                          {sec.totalCount}
                        </span>
                      )}
                    </button>
                  );
                })}
          </div>
        </div>
      </div>

      {/* ── SUBCATEGORY STRIP ── */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #f0f0f0",
          boxShadow: "0 1px 8px rgba(0,0,0,0.05)",
          position: "sticky",
          top: 64,
          zIndex: 40,
        }}
      >
        <div style={{ maxWidth: 1152, margin: "0 auto", padding: "0 16px" }}>
          <div
            className="cat-strip"
            style={{
              display: "flex",
              gap: 6,
              padding: "8px 0 6px",
              overflowX: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "#d1fae5 transparent",
            }}
          >
            {!sectionsReady
              ? [80, 110, 75, 95, 85].map((w, i) => (
                  <div
                    key={i}
                    style={{
                      height: 34,
                      width: w,
                      borderRadius: 100,
                      background: "#f0f0f0",
                      flexShrink: 0,
                      animation: "pulse 1.4s ease infinite",
                    }}
                  />
                ))
              : currentSection
                ? [
                    /* "All" pill */
                    <CatPill
                      key="all"
                      label={`All ${currentSection.label}`}
                      emoji={currentSection.emoji}
                      count={currentSection.totalCount}
                      active={activeCategory === "all"}
                      color={currentSection.color}
                      lightBg={currentSection.lightBg}
                      onClick={() => handleCategoryChange("all")}
                    />,
                    /* one pill per live subcategory */
                    ...currentSection.categories.map((cat) => (
                      <CatPill
                        key={cat.key}
                        label={cat.label}
                        emoji={cat.emoji}
                        count={cat.count}
                        active={activeCategory === cat.key}
                        color={currentSection.color}
                        lightBg={currentSection.lightBg}
                        onClick={() => handleCategoryChange(cat.key)}
                      />
                    )),
                    /* bestseller toggle */
                    <React.Fragment key="bs-sep">
                      <div
                        style={{
                          width: 1,
                          background: "#f0f0f0",
                          margin: "6px 4px",
                          flexShrink: 0,
                        }}
                      />
                      <button
                        onClick={() => setBestseller((b) => !b)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "7px 16px",
                          borderRadius: 100,
                          border: "1.5px solid",
                          borderColor: bestseller ? "#FF6B35" : "#e5e7eb",
                          background: bestseller
                            ? "rgba(255,107,53,0.08)"
                            : "#fff",
                          color: bestseller ? "#FF6B35" : "#6b7280",
                          fontSize: 12.5,
                          fontWeight: bestseller ? 700 : 500,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                          boxShadow: bestseller
                            ? "0 2px 10px rgba(255,107,53,0.2)"
                            : "none",
                          transition: "all 0.18s",
                        }}
                      >
                        ⭐ Bestsellers
                      </button>
                    </React.Fragment>,
                  ]
                : null}
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div
        style={{
          flex: 1,
          maxWidth: 1152,
          margin: "0 auto",
          padding: "20px 16px 40px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* loading */}
        {(!sectionsReady || loading) && !sectionsErr && <LoadingState />}

        {/* product fetch error */}
        {sectionsReady && !loading && error && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>😞</div>
            <p style={{ fontWeight: 700, color: "#374151", marginBottom: 8 }}>
              {error}
            </p>
            <button
              onClick={fetchProducts}
              style={{
                padding: "10px 24px",
                borderRadius: 12,
                border: "none",
                cursor: "pointer",
                background: "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                color: "#fff",
                fontWeight: 700,
              }}
            >
              Try Again
            </button>
          </div>
        )}

        {/* empty */}
        {sectionsReady &&
          !loading &&
          !error &&
          products.length === 0 &&
          currentSection && (
            <EmptyState
              section={currentSection}
              onReset={() => {
                handleCategoryChange("all");
                setBestseller(false);
                setSearchInput("");
              }}
              onHome={() => navigate("/")}
            />
          )}

        {/* products */}
        {sectionsReady &&
          !loading &&
          !error &&
          products.length > 0 &&
          currentSection && (
            <>
              {/* count bar */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 16,
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 3,
                      height: 18,
                      background: `linear-gradient(180deg,${currentSection.color},${currentSection.color}88)`,
                      borderRadius: 2,
                    }}
                  />
                  <h2
                    style={{
                      fontSize: 15,
                      fontWeight: 800,
                      color: "#111827",
                      margin: 0,
                    }}
                  >
                    {activeCatLabel}
                  </h2>
                </div>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "3px 10px",
                    borderRadius: 100,
                    background: currentSection.lightBg,
                    color: currentSection.color,
                    border: `1px solid ${currentSection.color}30`,
                  }}
                >
                  {products.length} items
                </span>
              </div>

              <div className="pgrid">
                {products.map((p) => (
                  <ProductCard key={p._id} product={p} />
                ))}
              </div>
            </>
          )}
      </div>

      <Footer />

      <style>{`
        .pgrid { display:grid; grid-template-columns:repeat(2,1fr); gap:12px; }
        @media(min-width:480px)  { .pgrid { grid-template-columns:repeat(3,1fr); gap:14px; } }
        @media(min-width:768px)  { .pgrid { grid-template-columns:repeat(3,1fr); gap:18px; } }
        @media(min-width:1024px) { .pgrid { grid-template-columns:repeat(4,1fr); gap:20px; } }

        .tabs-wrap::-webkit-scrollbar, div::-webkit-scrollbar { display:none; }

        .sec-tab:hover {
          background: rgba(255,255,255,0.12) !important;
          border-color: rgba(255,255,255,0.28) !important;
          color: rgba(255,255,255,0.88) !important;
        }

        /* thin minimal scrollbar for subcategory strip */
        .cat-strip::-webkit-scrollbar { height: 3px; }
        .cat-strip::-webkit-scrollbar-track { background: transparent; }
        .cat-strip::-webkit-scrollbar-thumb { background: #d1fae5; border-radius: 10px; }
        .cat-strip::-webkit-scrollbar-thumb:hover { background: #6ee7b7; }

        /* desktop ≥768 */
        @media(min-width:768px) {
          .hdr-top    { flex-wrap: nowrap; }
          .hdr-search { display: block   !important; }
          .mob-search { display: none    !important; }
          .tab-label  { display: inline  !important; }
          .sec-tab    { padding: 10px 20px 12px !important; font-size: 13.5px !important; }
        }
        /* tablet 481–767 */
        @media(min-width:481px) and (max-width:767px) {
          .hdr-top    { flex-wrap: wrap; }
          .hdr-search { display: none   !important; }
          .mob-search { display: block  !important; }
          .tabs-wrap  { width: 100%; }
          .tab-label  { display: inline !important; }
          .sec-tab    { padding: 8px 14px 10px !important; font-size: 12.5px !important; }
        }
        /* mobile ≤480: equal tabs filling full width, icon + label stacked, reduced height */
        @media(max-width:480px) {
          .hdr-top    { flex-wrap: wrap; margin-bottom: 10px !important; }
          .hdr-search { display: none  !important; }
          .mob-search { display: block !important; }
          .tabs-wrap  { width: 100%; overflow-x: visible !important; }
          .sec-tab {
            flex: 1 !important;
            flex-direction: column !important;
            gap: 2px !important;
            padding: 7px 4px 8px !important;
            align-items: center !important;
            justify-content: center !important;
            border-radius: 10px 10px 0 0 !important;
            font-size: 9.5px !important;
            min-width: 0 !important;
          }
          .sec-tab span:first-child { font-size: 18px !important; }
          .tab-label { display: block !important; font-size: 9.5px !important; font-weight: 700 !important; }
        }

        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse  { 0%,100%{opacity:0.7} 50%{opacity:0.35} }
        @keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes prog   { from{transform:translateX(-100%)} to{transform:translateX(250%)} }
        .fade-up { animation: fadeUp 0.4s ease both; }
      `}</style>
    </div>
  );
}

/* ── Category pill ─────────────────────────────────── */
function CatPill({ label, emoji, count, active, color, lightBg, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        padding: "7px 16px",
        borderRadius: 100,
        border: "1.5px solid",
        borderColor: active ? color : "#e5e7eb",
        background: active ? lightBg : "#fff",
        color: active ? color : "#6b7280",
        fontSize: 12.5,
        fontWeight: active ? 700 : 500,
        cursor: "pointer",
        whiteSpace: "nowrap",
        flexShrink: 0,
        boxShadow: active ? `0 2px 10px ${color}25` : "none",
        transition: "all 0.18s",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = color;
          e.currentTarget.style.color = color;
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = "#e5e7eb";
          e.currentTarget.style.color = "#6b7280";
        }
      }}
    >
      <span style={{ fontSize: 14 }}>{emoji}</span>
      {label}
      {count > 0 && (
        <span
          style={{
            fontSize: 9,
            fontWeight: 800,
            padding: "1px 5px",
            borderRadius: 20,
            background: active ? `${color}20` : "#f3f4f6",
            color: active ? color : "#9ca3af",
            marginLeft: 1,
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

/* ── Loading ────────────────────────────────────────── */
function LoadingState() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 0",
        gap: 10,
      }}
    >
      <div
        style={{ fontSize: 44, animation: "bounce 1s ease-in-out infinite" }}
      >
        🥤
      </div>
      <p style={{ fontWeight: 700, color: "#374151", fontSize: 14, margin: 0 }}>
        Preparing fresh menu…
      </p>
      <p style={{ color: "#9ca3af", fontSize: 12, margin: 0 }}>
        Fetching from our kitchen
      </p>
      <div
        style={{
          width: 160,
          height: 4,
          background: "#f0f0f0",
          borderRadius: 2,
          overflow: "hidden",
          marginTop: 8,
        }}
      >
        <div
          style={{
            height: "100%",
            width: "40%",
            background: "linear-gradient(90deg,#2D8B4E,#4CAF72)",
            borderRadius: 2,
            animation: "prog 1.4s ease-in-out infinite alternate",
          }}
        />
      </div>
    </div>
  );
}

/* ── Empty state ─────────────────────────────────────── */
function EmptyState({ section, onReset, onHome }) {
  return (
    <div
      className="fade-up"
      style={{
        textAlign: "center",
        padding: "70px 20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div style={{ fontSize: 56, marginBottom: 16 }}>🏜️</div>
      <h3
        style={{
          fontSize: 20,
          fontWeight: 800,
          color: "#111827",
          margin: "0 0 8px",
        }}
      >
        No items found
      </h3>
      <p
        style={{
          fontSize: 13.5,
          color: "#9ca3af",
          maxWidth: 300,
          lineHeight: 1.6,
          margin: "0 0 28px",
        }}
      >
        Nothing matches your filters. Try clearing them or browse all{" "}
        {section.label.toLowerCase()}.
      </p>
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <button
          onClick={onReset}
          style={{
            padding: "11px 24px",
            borderRadius: 12,
            border: "none",
            cursor: "pointer",
            background: `linear-gradient(135deg,${section.color},${section.color}cc)`,
            color: "#fff",
            fontWeight: 700,
            fontSize: 13.5,
            boxShadow: `0 4px 16px ${section.color}30`,
          }}
        >
          🔄 Reset Filters
        </button>
        <button
          onClick={onHome}
          style={{
            padding: "11px 24px",
            borderRadius: 12,
            cursor: "pointer",
            background: "#fff",
            border: "2px solid #e5e7eb",
            color: "#374151",
            fontWeight: 700,
            fontSize: 13.5,
          }}
        >
          🏠 Go Home
        </button>
      </div>
    </div>
  );
}
